import { useState, useEffect } from 'react';
import { useLocation, useSearch } from 'wouter';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { ArrowLeft, CreditCard, Lock, Check, Loader2, Tag, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { stripePromise } from '@/lib/stripe';
import { subscriptionService } from '@/services/api/subscription.service';
import { type Plan, type BillingCycle, type Currency, type PromoCodeValidationResponse, apiPlanToFrontendPlan } from '@/types/subscription';
import { formatCurrency, type Currency as CurrencyFormat } from '@/utils/currency';
import { MainLayout } from '@/layouts/main-layout';

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#9e2146',
    },
  },
};

interface CheckoutFormProps {
  plan: Plan;
  billingCycle: BillingCycle;
  currency: CurrencyFormat;
  validatedPromo: PromoCodeValidationResponse | null;
  onPromoValidated: (promo: PromoCodeValidationResponse | null) => void;
}

function CheckoutForm({ plan, billingCycle, currency, validatedPromo, onPromoValidated }: CheckoutFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { t } = useTranslation('billing');

  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [isValidatingPromo, setIsValidatingPromo] = useState(false);
  const [promoError, setPromoError] = useState<string | null>(null);

  const price = billingCycle === 'monthly' ? plan.price.monthly : plan.price.annual;
  const displayPrice = billingCycle === 'annual' ? price / 12 : price;

  // Handle promo code validation
  const handleApplyPromoCode = async () => {
    if (!promoCode.trim()) {
      setPromoError('Please enter a promo code');
      return;
    }

    setIsValidatingPromo(true);
    setPromoError(null);

    try {
      const result = await subscriptionService.validatePromoCode(promoCode.trim().toUpperCase());
      onPromoValidated(result);
      toast({
        title: t('toasts.promoAppliedTitle'),
        description: getDiscountDescription(result),
      });
    } catch (error: any) {
      setPromoError(error.response?.data?.error || 'Invalid promo code');
      onPromoValidated(null);
    } finally {
      setIsValidatingPromo(false);
    }
  };

  const handleRemovePromoCode = () => {
    setPromoCode('');
    onPromoValidated(null);
    setPromoError(null);
  };

  // Get human-readable discount description
  const getDiscountDescription = (promo: PromoCodeValidationResponse): string => {
    const { discount } = promo;
    if (discount.type === 'percent' && discount.percentOff) {
      return `${discount.percentOff}% off${discount.duration === 'once' ? ' first payment' : discount.duration === 'forever' ? ' forever' : ''}`;
    } else if (discount.type === 'amount' && discount.amountOff) {
      return `${formatCurrency(discount.amountOff / 100, currency)} off${discount.duration === 'once' ? ' first payment' : ''}`;
    }
    return 'Discount applied';
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    if (!acceptedTerms) {
      setErrorMessage('Please accept the terms and conditions');
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);

    try {
      // Get the CardElement
      const cardElement = elements.getElement(CardElement);

      if (!cardElement) {
        throw new Error('Card element not found');
      }

      // Create payment method
      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      if (!paymentMethod) {
        throw new Error('Failed to create payment method');
      }

      // Create subscription via backend
      await subscriptionService.createSubscription({
        planId: plan.id,
        billingCycle,
        paymentMethodId: paymentMethod.id,
        promotionCode: validatedPromo?.promotionCodeId,
      });

      // Invalidate subscription cache to fetch fresh data
      await queryClient.invalidateQueries({ queryKey: ['subscription'] });

      toast({
        title: t('toasts.subscriptionCreatedTitle'),
        description: t('toasts.subscriptionCreatedDescription', { plan: plan.displayName }),
      });

      // Redirect to billing settings
      setLocation('/admin/billing');
    } catch (error: any) {
      console.error('Checkout error:', error);
      setErrorMessage(error.message || 'An error occurred during checkout. Please try again.');

      toast({
        title: t('toasts.checkoutFailedTitle'),
        description: error.message || t('toasts.checkoutFailedDescription'),
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Card Input */}
      <div className="space-y-2">
        <Label htmlFor="card-element" className="flex items-center text-sm font-medium">
          <CreditCard className="w-4 h-4 mr-2" />
          Card Information
        </Label>
        <div className="border border-line rounded-md p-3 bg-white">
          <CardElement id="card-element" options={CARD_ELEMENT_OPTIONS} />
        </div>
        <p className="text-xs text-soft">
          Test card: 4242 4242 4242 4242, any future date, any CVC
        </p>
      </div>

      {/* Promo Code Section */}
      <div className="space-y-2">
        <Label htmlFor="promo-code" className="flex items-center text-sm font-medium">
          <Tag className="w-4 h-4 mr-2" />
          Promo Code (Optional)
        </Label>

        {validatedPromo ? (
          <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-md p-3">
            <div className="flex items-center">
              <Check className="w-4 h-4 text-green-600 mr-2" />
              <span className="text-sm text-green-800">
                <strong>{validatedPromo.code}</strong> - {getDiscountDescription(validatedPromo)}
              </span>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleRemovePromoCode}
              className="h-8 w-8 p-0"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        ) : (
          <div className="flex gap-2">
            <Input
              id="promo-code"
              placeholder="Enter promo code"
              value={promoCode}
              onChange={(e) => {
                setPromoCode(e.target.value.toUpperCase());
                setPromoError(null);
              }}
              className="flex-1"
            />
            <Button
              type="button"
              variant="outline"
              onClick={handleApplyPromoCode}
              disabled={isValidatingPromo || !promoCode.trim()}
            >
              {isValidatingPromo ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                'Apply'
              )}
            </Button>
          </div>
        )}

        {promoError && (
          <p className="text-sm text-red-600">{promoError}</p>
        )}
      </div>

      {/* Error Message */}
      {errorMessage && (
        <Alert variant="destructive">
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}

      {/* Terms and Conditions */}
      <div className="flex items-start space-x-2">
        <Checkbox
          id="terms"
          checked={acceptedTerms}
          onCheckedChange={(checked) => setAcceptedTerms(checked as boolean)}
        />
        <Label htmlFor="terms" className="text-sm text-soft cursor-pointer">
          I agree to the{' '}
          <a href="/terms" className="text-blue-600 hover:underline">
            Terms of Service
          </a>{' '}
          and{' '}
          <a href="/privacy" className="text-blue-600 hover:underline">
            Privacy Policy
          </a>
        </Label>
      </div>

      {/* Submit Button */}
      <Button
        type="submit"
        className="w-full bg-blue-600 hover:bg-blue-700 text-white"
        size="lg"
        disabled={!stripe || isProcessing || !acceptedTerms}
      >
        {isProcessing ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
            Processing...
          </>
        ) : (
          <>
            <Lock className="w-4 h-4 mr-2" />
            {plan.trialDays > 0
              ? `Start ${plan.trialDays}-Day Free Trial`
              : `Subscribe for ${formatCurrency(displayPrice, currency)}/month`}
          </>
        )}
      </Button>

      {/* Security Message */}
      <p className="text-xs text-center text-soft">
        <Lock className="w-3 h-3 inline mr-1" />
        Payments are secure and encrypted. Powered by Stripe.
      </p>
    </form>
  );
}

export default function CheckoutPage() {
  const searchString = useSearch();
  const [, setLocation] = useLocation();
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const [validatedPromo, setValidatedPromo] = useState<PromoCodeValidationResponse | null>(null);

  // Parse query params
  const params = new URLSearchParams(searchString);
  const planId = params.get('plan');
  const cycle = params.get('cycle') as BillingCycle | null;
  const urlCurrency = params.get('currency')?.toLowerCase() as Currency | undefined;

  // Set billing cycle from URL
  useEffect(() => {
    if (cycle === 'annual' || cycle === 'monthly') {
      setBillingCycle(cycle);
    }
  }, [cycle]);

  // Fetch plans from API with correct currency
  const { data: plansResponse, isLoading } = useQuery({
    queryKey: ['subscription', 'plans', urlCurrency],
    queryFn: () => subscriptionService.getPlans(urlCurrency),
    staleTime: 1000 * 60 * 5,
  });

  // Get currency from API response
  const currency: Currency = plansResponse?.meta?.currency || urlCurrency || 'usd';
  const currencyFormat: CurrencyFormat = currency.toUpperCase() as CurrencyFormat;

  // Find selected plan from API response
  const plan: Plan | undefined = plansResponse?.data
    ?.map(apiPlanToFrontendPlan)
    .find((p) => p.id === planId);

  // Redirect if no plan ID or invalid plan
  useEffect(() => {
    if (!planId) {
      setLocation('/pricing');
    } else if (!isLoading && plansResponse && !plan) {
      setLocation('/pricing');
    } else if (plan?.name === 'free') {
      setLocation('/pricing');
    }
  }, [planId, plan, isLoading, plansResponse, setLocation]);

  if (isLoading || !plan) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        </div>
      </MainLayout>
    );
  }

  const price = billingCycle === 'monthly' ? plan.price.monthly : plan.price.annual;
  const displayPrice = billingCycle === 'annual' ? price / 12 : price;

  // Calculate discounted price if promo code is applied
  const calculateDiscountedPrice = (originalPrice: number): number => {
    if (!validatedPromo) return originalPrice;
    const { discount } = validatedPromo;
    if (discount.type === 'percent' && discount.percentOff) {
      return originalPrice * (1 - discount.percentOff / 100);
    } else if (discount.type === 'amount' && discount.amountOff) {
      // amountOff is in cents from Stripe
      return Math.max(0, originalPrice - discount.amountOff / 100);
    }
    return originalPrice;
  };

  const discountedPrice = calculateDiscountedPrice(billingCycle === 'annual' ? price : displayPrice);
  const discountedDisplayPrice = billingCycle === 'annual' ? calculateDiscountedPrice(price) / 12 : discountedPrice;
  const hasDiscount = validatedPromo && discountedPrice !== (billingCycle === 'annual' ? price : displayPrice);

  return (
    <MainLayout>
      <div className="min-h-screen bg-surface-2 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <Button
            variant="ghost"
            onClick={() => setLocation('/pricing')}
            className="mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Pricing
          </Button>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Order Summary */}
            <div>
              <h2 className="text-h1 text-ink mb-6">
                Order Summary
              </h2>

              <Card>
                <CardHeader>
                  <CardTitle>{plan.displayName} Plan</CardTitle>
                  <CardDescription>{plan.description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Price Breakdown */}
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-soft">
                        {billingCycle === 'monthly' ? 'Monthly' : 'Annual'} Subscription
                      </span>
                      <span className="font-medium">
                        {formatCurrency(displayPrice, currencyFormat)}/month
                      </span>
                    </div>
                    {billingCycle === 'annual' && (
                      <div className="flex justify-between text-sm">
                        <span className="text-soft">
                          Billed Annually
                        </span>
                        <span className="font-medium">{formatCurrency(price, currencyFormat)}</span>
                      </div>
                    )}
                  </div>

                  {/* Discount Row */}
                  {hasDiscount && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span className="flex items-center">
                        <Tag className="w-4 h-4 mr-1" />
                        Discount ({validatedPromo?.code})
                      </span>
                      <span>
                        {validatedPromo?.discount.type === 'percent'
                          ? `-${validatedPromo.discount.percentOff}%`
                          : `-${formatCurrency((validatedPromo?.discount.amountOff || 0) / 100, currencyFormat)}`}
                      </span>
                    </div>
                  )}

                  <div className="border-t pt-4">
                    <div className="flex justify-between text-lg font-bold">
                      <span>Total</span>
                      <span>
                        {plan.trialDays > 0 ? (
                          `${formatCurrency(0, currencyFormat)} (${plan.trialDays}-day trial)`
                        ) : hasDiscount ? (
                          <span className="flex items-center gap-2">
                            <span className="text-soft line-through text-base font-normal">
                              {formatCurrency(billingCycle === 'annual' ? price : displayPrice, currencyFormat)}
                            </span>
                            <span className="text-green-600">
                              {formatCurrency(discountedPrice, currencyFormat)}
                            </span>
                          </span>
                        ) : (
                          formatCurrency(billingCycle === 'annual' ? price : displayPrice, currencyFormat)
                        )}
                      </span>
                    </div>
                    {plan.trialDays > 0 && (
                      <p className="text-sm text-soft mt-2">
                        Your card will be charged{' '}
                        {hasDiscount ? (
                          <>
                            <span className="line-through">{formatCurrency(billingCycle === 'annual' ? price : displayPrice, currencyFormat)}</span>
                            {' '}
                            <span className="text-green-600 font-medium">{formatCurrency(discountedPrice, currencyFormat)}</span>
                          </>
                        ) : (
                          formatCurrency(billingCycle === 'annual' ? price : displayPrice, currencyFormat)
                        )}{' '}
                        after the {plan.trialDays}-day trial ends
                      </p>
                    )}
                    {hasDiscount && validatedPromo?.discount.duration === 'once' && (
                      <p className="text-xs text-soft mt-1">
                        * Discount applies to first payment only
                      </p>
                    )}
                  </div>

                  {/* Features */}
                  <div className="border-t pt-4">
                    <p className="font-medium mb-3">What's included:</p>
                    <ul className="space-y-2">
                      {plan.features.additionalFeatures.slice(0, 4).map((feature, index) => (
                        <li key={index} className="flex items-start text-sm">
                          <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                          <span className="text-ink">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Payment Form */}
            <div>
              <h2 className="text-h1 text-ink mb-6">
                Payment Details
              </h2>

              <Card>
                <CardHeader>
                  <CardTitle>Complete Your Subscription</CardTitle>
                  <CardDescription>
                    Enter your payment information to get started
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Elements stripe={stripePromise}>
                    <CheckoutForm
                      plan={plan}
                      billingCycle={billingCycle}
                      currency={currencyFormat}
                      validatedPromo={validatedPromo}
                      onPromoValidated={setValidatedPromo}
                    />
                  </Elements>
                </CardContent>
              </Card>

              {/* Money-back Guarantee */}
              <div className="mt-6 text-center">
                <div className="inline-flex items-center px-4 py-2 bg-green-50 rounded-full">
                  <Check className="w-4 h-4 text-green-600 mr-2" />
                  <span className="text-sm text-green-800 font-medium">
                    30-day money-back guarantee
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
